import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { EmailTemplateManagerGuard } from 'app/core/auth/guards/email-template-manager.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { SyzlEmailGuard } from 'app/core/auth/guards/syzl-email.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/my-dashboard'
    {path: '', pathMatch : 'full', redirectTo: 'my-dashboard'},

    // Redirect signed-in user to the '/my-dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'my-dashboard'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

// Admin routes
{
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
        initialData: initialDataResolver
    },

    children: [
      {
        path: 'my-dashboard',
        loadChildren: () => import('app/modules/admin/analytics/dashboard/my-dashboard.routes').then(m => m.MY_DASHBOARD_ROUTES)
      },
      {
        path: 'dashboard',
        loadChildren: () => import('app/modules/admin/analytics/dashboard/dashboard-team.routes').then(m => m.DASHBOARD_TEAM_ROUTES)
      },
      {
        path: 'users',
        data: {
          type: 'super_admin'
        },
        loadChildren: () => import('app/modules/admin/user/user.module').then(m => m.UserModule)
      },
      {
        path: 'user-completion',
        data: {
          type: 'super_admin'
        },
        loadChildren: () => import('app/modules/admin/user-completion/user-completion.module').then(m => m.UserCompletionModule)
      },
      {
        path: 'customers',
        data: {
          type: 'customer'
        },
        loadChildren: () => import('app/modules/admin/user/user.module').then(m => m.UserModule)
      },
      {
        path: 'vendors',
        data: {
          type: 'vendor'
        },
        loadChildren: () => import('app/modules/admin/vendor/vendor.module').then(m => m.VendorModule)
      },
      {
        path: 'icp',
        loadChildren: () => import('app/modules/admin/icp/icp.module').then(m => m.IcpModule)
      },
      {
        path: 'icp-landing-page',
        loadChildren: () => import('app/modules/admin/icp-landing-page/icp-landing-page.module').then(m => m.IcpLandingPageModule)
      },
      {
        path: 'customer-credits',
        loadChildren: () => import('app/modules/admin/customer-credits/customer-credits.module').then(m => m.CustomerCreditsModule)
      },
      {
        path: 'categories',
        loadChildren: () => import('app/modules/admin/category/category.module').then(m => m.CategoryModule)
      },
      {
        path: 'conversations',
        loadChildren: () => import('app/modules/admin/conversations/conversations.module').then(m => m.ConversationsModule)
      },
      {
        path: 'calls',
        loadChildren: () => import('app/modules/admin/calls/calls.module').then(m => m.CallsModule)
      },
      {
        path: 'leads',
        loadChildren: () => import('app/modules/admin/leads/leads.module').then(m => m.LeadsModule)
      },
      {
        path: 'lifecycle',
        loadComponent: () => import('app/modules/admin/lifecycle/lifecycle.component').then(m => m.MakerLifecycleComponent)
      },
      {
        path: 'onboarding',
        loadComponent: () => import('app/modules/admin/onboarding/onboarding.component').then(m => m.OnboardingComponent)
      },
      {
        path: 'bookings',
        loadChildren: () => import('app/modules/admin/bookings/bookings.module').then(m => m.BookingsModule)
      },
      {
        path: 'booking-fees',
        loadChildren: () => import('app/modules/admin/booking-fees/booking-fees.module').then(m => m.BookingFeesModule)
      },
      {
        path: 'email',
        loadChildren: () => import('app/modules/admin/email/email.module').then(m => m.EmailModule)
      },
      {
        path: 'email-key',
        loadChildren: () => import('app/modules/admin/email-key/email-key.module').then(m => m.EmailKeyModule)
      },
      {
        path: 'bulk-companies',
        loadChildren: () => import('app/modules/admin/enterprise-companies/enterprise-companies.module').then(m => m.EnterpriseCompaniesModule)
      },
      {
        path: 'bulk-deals',
        loadChildren: () => import('app/modules/admin/enterprise-deals/enterprise-deals.module').then(m => m.EnterpriseDealsModule)
      },
      {
        path: 'bulk-bookings',
        loadChildren: () => import('app/modules/admin/enterprise-bookings/enterprise-bookings.module').then(m => m.EnterpriseBookingsModule)
      },
      {
        path: 'equipment-categories',
        loadChildren: () => import('app/modules/admin/equipment-category/equipment-category.module').then(m => m.EquipmentCategoryModule)
      },
      {
        path: 'features',
        loadChildren: () => import('app/modules/admin/feature/feature.module').then(m => m.FeatureModule)
      },
      {
        path: 'booking-add-on-categories',
        loadChildren: () => import('app/modules/admin/booking-add-on-categories/booking-add-on-categories.module').then(m => m.BookingAddOnCategoriesModule)
      },
      {
        path: 'booking-add-on-templates',
        loadChildren: () => import('app/modules/admin/booking-add-on-templates/booking-add-on-templates.module').then(m => m.BookingAddOnTemplatesModule)
      },
      {
        path: 'booking-add-ons',
        loadChildren: () => import('app/modules/admin/booking-add-ons/booking-add-ons.module').then(m => m.BookingAddOnsModule)
      },
      {
        path: 'booking-failed-payments',
        loadChildren: () => import('app/modules/admin/booking-failed-payment/booking-failed-payment.module').then(m => m.BookingFailedPaymentModule)
      },
      {
        path: 'booking-failed-transfers',
        loadChildren: () => import('app/modules/admin/booking-failed-transfer/booking-failed-transfer.module').then(m => m.BookingFailedTransferModule)
      },
      {
        path: 'booking-rules',
        loadChildren: () => import('app/modules/admin/booking-rules/booking-rules.module').then(m => m.BookingRulesModule)
      },
      {
        path: 'tickets',
        loadChildren: () => import('app/modules/admin/tickets/tickets.module').then(m => m.TicketsModule)
      },
      {
        path: 'customer-360',
        loadChildren: () => import('app/modules/admin/customer-360/customer-360.module').then(m => m.Customer360Module)
      },
      {
        path: 'flagged',
        loadChildren: () => import('app/modules/admin/flagged/flagged.module').then(m => m.FlaggedModule)
      },
      {
        path: 'dietary-restriction',
        loadChildren: () => import('app/modules/admin/dietary-restriction/dietary-restriction.module').then(m => m.DietaryRestrictionModule)
      },
      {
        path: 'posts',
        loadChildren: () => import('app/modules/admin/post/post.module').then(m => m.PostModule)
      },
      {
        path: 'pages',
        loadChildren: () => import('app/modules/admin/page/page.module').then(m => m.PageModule)
      },
      {
        path: 'help-center',
        loadChildren: () => import('app/modules/admin/help-center/help-center.routes').then(m => m.routes)
      },
      {
        path: 'slider-images',
        loadChildren: () => import('app/modules/admin/slider-image/slider-image.module').then(m => m.SliderImageModule)
      },
      {
        path: 'slides-intro',
        loadChildren: () => import('app/modules/admin/slide-intro/slide-intro.module').then(m => m.SlideIntroModule)
      },
      {
        path: 'packages',
        loadChildren: () => import('app/modules/admin/package/package.module').then(m => m.PackageModule)
      },
      {
        path: 'user-insurance',
        loadChildren: () => import('app/modules/admin/user-insurance/user-insurance.module').then(m => m.UserInsuranceModule)
      },
      {
        path: 'fuse',
        loadChildren: () => import('app/modules/admin/fuse/fuse.module').then(m => m.FuseModule)
      },
      {
        path: 'syzl-subscriptions',
        loadChildren: () => import('app/modules/admin/syzl-subscriptions/syzl-subscriptions.module').then(m => m.SyzlSubscriptionsModule)
      },
      {
        path: 'user-food-safety',
        loadChildren: () => import('app/modules/admin/user-food-safety/user-food-safety.module').then(m => m.UserFoodSafetyModule)
      },
      {
        path: 'user-packages',
        loadChildren: () => import('app/modules/admin/user-package/user-package.module').then(m => m.UserPackageModule)
      },
      {
        path: 'places',
        loadChildren: () => import('app/modules/admin/place/place.module').then(m => m.PlaceModule)
      },
      {
        path: 'place-city',
        loadChildren: () => import('app/modules/admin/place-city/place-city.module').then(m => m.PlaceCityModule)
      },
      {
        path: 'request-city',
        loadChildren: () => import('app/modules/admin/request-city/request-city.module').then(m => m.RequestCityModule)
      },
      {
        path: 'promo-codes',
        loadChildren: () => import('app/modules/admin/promo-codes/promo-codes.module').then(m => m.PromoCodesModule)
      },
      {
        path: 'facilities',
        loadChildren: () => import('app/modules/admin/facility/facility.module').then(m => m.FacilityModule)
      },
      {
        path: 'facility-document-types',
        loadChildren: () => import('app/modules/admin/facility-document-types/facility-document-type.module').then(m => m.FacilityDocumentTypeModule)
      },
      {
        path: 'reviews',
        loadChildren: () => import('app/modules/admin/review/review.module').then(m => m.ReviewModule)
      },
      {
        path: 'notifications',
        loadChildren: () => import('app/modules/admin/notification/notification.module').then(m => m.NotificationModule)
      },
      {
        path: 'notification-templates',
        loadChildren: () => import('app/modules/admin/notification-template/notification-template.module').then(m => m.NotificationTemplateModule)
      },
      {
        path: 'settings',
        loadChildren: () => import('app/modules/admin/settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'smarter-u-account',
        loadChildren: () => import('app/modules/admin/smarter-u-account/smarter-u-account.module').then(m => m.SmarterUAccountModule)
      },
      {
        path: 'smarter-u-dummy',
        loadChildren: () => import('app/modules/admin/smarter-u-dummy/smarter-u-dummy.module').then(m => m.SmarterUDummyModule)
      },
      {
        path: 'twilio-numbers',
        loadChildren: () => import('app/modules/admin/twilio-numbers/twilio-numbers.module').then(m => m.TwilioNumbersModule)
      },
      {
        path: 'aircall-lines',
        loadChildren: () => import('app/modules/admin/aircall-lines/aircall-lines.module').then(m => m.AircallLinesModule)
      },
      {
        path: 'admin/stripe-accounts',
        loadChildren: () => import('app/modules/admin/stripe-accounts/stripe-accounts.module').then(m => m.StripeAccountsModule)
      },
      {
        path: 'admin/stripe-customers',
        loadChildren: () => import('app/modules/admin/stripe-customers/stripe-customers.module').then(m => m.StripeCustomersModule)
      },
      {
        path: 'completion-requirements',
        loadChildren: () => import('app/modules/admin/completion-requirements/completion-requirements.module').then(m => m.CompletionRequirementsModule)
      },
      {
        path: 'financial-overview',
        loadChildren: () => import('app/modules/admin/financial-overview/financial-overview.module').then(m => m.FinancialOverviewModule)
      },
      {
        path: 'mongo-charts',
        canActivate: [SyzlEmailGuard],
        loadChildren: () => import('app/modules/admin/mongo-charts/mongo-charts.module').then(m => m.MongoChartsModule)
      },
      {
        path: 'email-templates',
        canActivate: [EmailTemplateManagerGuard],
        loadChildren: () => import('app/modules/admin/email-templates/email-templates.module').then(m => m.EmailTemplatesModule)
      },
      {
        path: 'analytics/kitchens',
        loadChildren: () => import('app/modules/admin/analytics/kitchens/kitchens.routes').then(m => m.KITCHENS_ROUTES)
      },
      {
        path: 'email-analytics',
        canActivate: [EmailTemplateManagerGuard],
        loadChildren: () => import('app/modules/admin/email-analytics/email-analytics.module').then(m => m.EmailAnalyticsModule)
      },
      {
        path: 'analytics/bookings',
        loadChildren: () => import('app/modules/admin/analytics/bookings/analytics-bookings.routes').then(m => m.ANALYTICS_BOOKINGS_ROUTES)
      },
    ]
  }
];
